import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenantConfig } from '../../../hooks/useTenantConfig';
import { useTenantProtection } from '../../../hooks/useTenantProtection';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../../contexts/ThemeContext';
import {
  Database,
  Settings,
  Play,
  Pause,
  RefreshCw,
  Trash2,
  CheckCircle,
  XCircle,
  Clock,
  BarChart3,
  Search,
  Info,
  AlertCircle,
  Download,
  Upload,
  Eye,
  EyeOff
} from 'lucide-react';

const RAGManagement = () => {
  const { user, getCurrentTenant } = useAuth();
  const { tenantConfig } = useTenantConfig();
  const { getProtectedTenantCode } = useTenantProtection();
  const navigate = useNavigate();
  const { isDark } = useTheme();

  // Check if user has admin access
  useEffect(() => {
    if (user) {
      let hasAdminAccess = false;
      
      // For admin users, check userType and role
      if (user.userType === 'admin') {
        hasAdminAccess = user.role === 'Admin' || user.isAdmin;
      } else {
        // For regular users, check roles array
        hasAdminAccess = user.roles && user.roles.some(role => role.RoleName === 'Admin');
      }
      
      if (!hasAdminAccess) {
        navigate('/dashboard');
        return;
      }
    }
  }, [user, navigate]);

  // State management
  const [ragStatus, setRagStatus] = useState(null);
  const [datasets, setDatasets] = useState([]);
  const [datasetStatistics, setDatasetStatistics] = useState([]);
  const [ingestionStatus, setIngestionStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedCollection, setSelectedCollection] = useState('CRM');
  const [tenantCollections, setTenantCollections] = useState([]);

  // Change tracking state
  const [changeStatistics, setChangeStatistics] = useState(null);
  const [recentChanges, setRecentChanges] = useState([]);
  const [changeProcessingStatus, setChangeProcessingStatus] = useState(null);
  const [schedules, setSchedules] = useState([]);

  // Configuration state
  const [datasetConfig, setDatasetConfig] = useState({});
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);

  // Auto-refresh state
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(null);

  // Tag selection state
  const [availableTags, setAvailableTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [tagsLoading, setTagsLoading] = useState(false);

  // Statistics state
  const [statisticsLoading, setStatisticsLoading] = useState(false);
  const [expandedStatistics, setExpandedStatistics] = useState({});

  // Helper function to get tenant code
  const getTenantCode = useCallback(() => {
    return getProtectedTenantCode();
  }, [getProtectedTenantCode]);

  // Helper function to generate tenant-specific collection name
  const getTenantCollectionName = useCallback((baseCollection) => {
    const tenantCode = getTenantCode();
    return `${tenantCode}_${baseCollection}`;
  }, [getTenantCode]);

  // Initialize tenant-specific collections
  useEffect(() => {
    const tenantCode = getTenantCode();
    if (tenantCode && tenantCode !== 'DEFAULT') {
      const collections = [
        {
          id: 'CRM',
          name: `${tenantCode}_CRM`,
          displayName: 'CRM (All Data)',
          description: 'Complete CRM data including customers, leads, opportunities, and activities'
        },
        {
          id: 'Sales',
          name: `${tenantCode}_Sales`,
          displayName: 'Sales (Knowledge Articles)',
          description: 'Sales-focused knowledge articles and related content'
        }
      ];
      setTenantCollections(collections);
      
      // Set default collection to tenant-specific CRM
      setSelectedCollection(collections[0].name);
    } else {
      // Fallback for users without proper tenant context
      const defaultCollections = [
        {
          id: 'CRM',
          name: 'DEFAULT_CRM',
          displayName: 'CRM (All Data)',
          description: 'Complete CRM data including customers, leads, opportunities, and activities'
        }
      ];
      setTenantCollections(defaultCollections);
      setSelectedCollection(defaultCollections[0].name);
    }
  }, [getTenantCode]);

  // Fetch RAG system status
  const fetchRagStatus = useCallback(async () => {
    try {
      const response = await axios.get('/api/rag/status');
      setRagStatus(response.data.data);
    } catch (error) {
      console.error('Error fetching RAG status:', error);
      setError('Failed to fetch RAG system status');
    }
  }, []);

  // Fetch datasets
  const fetchDatasets = useCallback(async () => {
    try {
      const response = await axios.get('/api/rag/datasets');
      setDatasets(response.data.data.datasets);
      
      // Initialize dataset config
      const config = {};
      response.data.data.datasets.forEach(dataset => {
        config[dataset.name] = dataset.enabled;
      });
      setDatasetConfig(config);
    } catch (error) {
      console.error('Error fetching datasets:', error);
      setError('Failed to fetch datasets');
    }
  }, []);

  // Fetch detailed dataset statistics
  const fetchDatasetStatistics = useCallback(async () => {
    setStatisticsLoading(true);
    try {
      const response = await axios.get('/api/rag/datasets/statistics');
      setDatasetStatistics(response.data.data.statistics);
    } catch (error) {
      console.error('Error fetching dataset statistics:', error);
      setError('Failed to fetch dataset statistics');
    } finally {
      setStatisticsLoading(false);
    }
  }, []);

  // Fetch change tracking statistics
  const fetchChangeStatistics = useCallback(async () => {
    try {
      const response = await axios.get('/api/rag/changes/statistics');
      setChangeStatistics(response.data.data);
    } catch (error) {
      console.error('Error fetching change statistics:', error);
    }
  }, []);

  // Fetch recent changes
  const fetchRecentChanges = useCallback(async () => {
    try {
      const response = await axios.get('/api/rag/changes/recent?limit=20');
      setRecentChanges(response.data.data.changes);
    } catch (error) {
      console.error('Error fetching recent changes:', error);
    }
  }, []);

  // Fetch change processing status
  const fetchChangeProcessingStatus = useCallback(async () => {
    try {
      const response = await axios.get('/api/rag/changes/status');
      setChangeProcessingStatus(response.data.data);
    } catch (error) {
      console.error('Error fetching change processing status:', error);
    }
  }, []);

  // Fetch processing schedules
  const fetchSchedules = useCallback(async () => {
    try {
      const response = await axios.get('/api/rag/changes/schedules');
      setSchedules(response.data.data.schedules);
    } catch (error) {
      console.error('Error fetching schedules:', error);
    }
  }, []);

  // Fetch ingestion status
  const fetchIngestionStatus = useCallback(async () => {
    try {
      const response = await axios.get('/api/rag/ingest/status');
      setIngestionStatus(response.data.data);
    } catch (error) {
      console.error('Error fetching ingestion status:', error);
    }
  }, []);

  // Load all data
  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      await Promise.all([
        fetchRagStatus(),
        fetchDatasets(),
        fetchDatasetStatistics(),
        fetchIngestionStatus(),
        fetchChangeStatistics(),
        fetchRecentChanges(),
        fetchChangeProcessingStatus(),
        fetchSchedules()
      ]);
    } catch (error) {
      setError('Failed to load RAG management data');
    } finally {
      setLoading(false);
    }
  }, [fetchRagStatus, fetchDatasets, fetchDatasetStatistics, fetchIngestionStatus, fetchChangeStatistics, fetchRecentChanges, fetchChangeProcessingStatus, fetchSchedules]);

  // Fetch available tags from knowledge articles
  const fetchAvailableTags = useCallback(async () => {
    setTagsLoading(true);
    console.log('Fetching available tags...');
    
    try {
      const response = await axios.get('/api/knowledge/tags');
      console.log('Tags response:', response.data);
      
      const tags = response.data.tags || [];
      setAvailableTags(tags);
      
      // Initialize with pre-selected sales tags if none selected
      if (selectedTags.length === 0 && tags.length > 0) {
        const salesKeywords = ['sales', 'product', 'pricing', 'proposal', 'quote', 'demo', 'pitch', 'negotiation', 'contract', 'revenue', 'customer', 'lead', 'opportunity', 'prospect', 'deal', 'close', 'marketing', 'campaign', 'promotion', 'discount'];
        const matchingSalesTags = tags.filter(tag => 
          salesKeywords.some(keyword => 
            tag.name.toLowerCase().includes(keyword.toLowerCase())
          )
        );
        
        console.log('Auto-selecting sales-related tags:', matchingSalesTags);
        setSelectedTags(matchingSalesTags.map(tag => tag.name));
      }
      
      if (tags.length === 0) {
        console.warn('No tags found - there may be no knowledge articles yet');
      }
      
    } catch (error) {
      console.error('Error fetching tags:', error);
      const errorMessage = error.response?.data?.details || error.message || 'Unknown error';
      setError(`Failed to fetch available tags: ${errorMessage}`);
      
      // Clear any existing tags on error
      setAvailableTags([]);
    } finally {
      setTagsLoading(false);
    }
  }, [selectedTags.length]);

  // Initial load
  useEffect(() => {
    const tenantCode = getProtectedTenantCode();
    if (tenantCode) {
      loadData();
      fetchAvailableTags();
      fetchDatasetStatistics();
    }
  }, [loadData, fetchAvailableTags, fetchDatasetStatistics, getProtectedTenantCode]);

  // Auto-refresh functionality
  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        fetchIngestionStatus();
        if (ingestionStatus?.current_status === 'in_progress') {
          fetchRagStatus();
        }
      }, 15000); // Refresh every 15 seconds when auto-refresh is on

      setRefreshInterval(interval);
      return () => clearInterval(interval);
    } else if (refreshInterval) {
      clearInterval(refreshInterval);
      setRefreshInterval(null);
    }
  }, [autoRefresh, fetchIngestionStatus, fetchRagStatus, ingestionStatus?.current_status]);

  // Update dataset configuration
  const updateDatasetConfig = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await axios.put('/api/rag/datasets/config', {
        datasets: datasetConfig
      });
      
      setSuccess('Dataset configuration updated successfully');
      await fetchDatasets();
    } catch (error) {
      console.error('Error updating dataset config:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.details || error.message || 'Failed to update dataset configuration';
      const errorDetails = error.response?.data?.details;
      setError(errorDetails ? `${errorMessage}: ${errorDetails}` : errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Start data ingestion
  const startIngestion = async (forceRefresh = false) => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const ingestionData = {
        force_refresh: forceRefresh,
        collection: selectedCollection
      };

      // Include selected tags and categories for Sales collection
      if (getCollectionType(selectedCollection) === 'Sales' && selectedTags.length > 0) {
        // Separate tags and categories
        const selectedCategories = selectedTags.filter(tagName => {
          const item = availableTags.find(t => t.name === tagName);
          return item?.type === 'category';
        });
        
        const selectedTagsOnly = selectedTags.filter(tagName => {
          const item = availableTags.find(t => t.name === tagName);
          return item?.type === 'tag';
        });

        if (selectedCategories.length > 0) {
          ingestionData.selectedCategories = selectedCategories;
        }
        if (selectedTagsOnly.length > 0) {
          ingestionData.selectedTags = selectedTagsOnly;
        }
      }

      const response = await axios.post('/api/rag/ingest', ingestionData);
      
      setSuccess(`Data ingestion started successfully for ${selectedCollection} collection. ${response.data.data.estimated_duration}`);
      setAutoRefresh(true); // Start auto-refresh to monitor progress
      await fetchIngestionStatus();
    } catch (error) {
      console.error('Error starting ingestion:', error);
      setError(error.response?.data?.error || 'Failed to start data ingestion');
    } finally {
      setLoading(false);
    }
  };

  // Clear RAG data
  const clearData = async () => {
    if (!window.confirm(`Are you sure you want to clear all ${selectedCollection} collection data? This action cannot be undone.`)) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await axios.delete('/api/rag/data', {
        data: { collection: selectedCollection }
      });
      setSuccess(`${selectedCollection} collection data cleared successfully`);
      await loadData();
    } catch (error) {
      console.error('Error clearing RAG data:', error);
      setError(error.response?.data?.error || 'Failed to clear RAG data');
    } finally {
      setLoading(false);
    }
  };

  // Search RAG data
  const searchRAG = async () => {
    if (!searchQuery.trim()) return;

    setSearchLoading(true);
    setError('');

    try {
      const response = await axios.post('/api/rag/search', {
        query: searchQuery,
        collection: selectedCollection,
        limit: 10,
        filters: {}
      });
      
      setSearchResults(response.data.data);
    } catch (error) {
      console.error('Error searching RAG data:', error);
      setError(error.response?.data?.error || `Failed to search ${selectedCollection} data`);
    } finally {
      setSearchLoading(false);
    }
  };

  // Toggle dataset enabled state
  const toggleDataset = (datasetName) => {
    setDatasetConfig(prev => ({
      ...prev,
      [datasetName]: !prev[datasetName]
    }));
  };

  // Toggle tag selection
  const toggleTag = (tagName) => {
    setSelectedTags(prev => {
      if (prev.includes(tagName)) {
        return prev.filter(tag => tag !== tagName);
      } else {
        return [...prev, tagName];
      }
    });
  };

  // Select all tags
  const selectAllTags = () => {
    setSelectedTags(availableTags.map(tag => tag.name));
  };

  // Clear all tags
  const clearAllTags = () => {
    setSelectedTags([]);
  };

  const toggleStatisticsExpansion = (datasetName) => {
    setExpandedStatistics(prev => ({
      ...prev,
      [datasetName]: !prev[datasetName]
    }));
  };

  // Create sample knowledge articles for testing
  const createSampleData = async () => {
    setTagsLoading(true);
    try {
      const response = await axios.post('/api/knowledge/create-sample-data');
      setSuccess(`Sample data created: ${response.data.data.created} articles created, ${response.data.data.skipped} skipped`);
      
      // Refresh tags after creating sample data
      setTimeout(() => {
        fetchAvailableTags();
      }, 1000);
      
    } catch (error) {
      console.error('Error creating sample data:', error);
      const errorMessage = error.response?.data?.details || error.message || 'Unknown error';
      setError(`Failed to create sample data: ${errorMessage}`);
    } finally {
      setTagsLoading(false);
    }
  };

  // Manual trigger change processing
  const triggerChangeProcessing = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.post('/api/rag/changes/process');
      setSuccess(response.data.message);
      
      // Refresh data after processing
      setTimeout(() => {
        fetchChangeStatistics();
        fetchRecentChanges();
      }, 2000);
      
    } catch (error) {
      console.error('Error triggering change processing:', error);
      setError(error.response?.data?.error || 'Failed to trigger change processing');
    } finally {
      setLoading(false);
    }
  };

  // Clean up old changes
  const cleanupOldChanges = async (daysToKeep = 7) => {
    if (!window.confirm(`Are you sure you want to clean up changes older than ${daysToKeep} days?`)) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.post('/api/rag/changes/cleanup', { daysToKeep });
      setSuccess(response.data.message);
      
      // Refresh data after cleanup
      setTimeout(() => {
        fetchChangeStatistics();
        fetchRecentChanges();
      }, 1000);
      
    } catch (error) {
      console.error('Error cleaning up old changes:', error);
      setError(error.response?.data?.error || 'Failed to clean up old changes');
    } finally {
      setLoading(false);
    }
  };

  // Restart change processing service
  const restartChangeService = async () => {
    if (!window.confirm('Are you sure you want to restart the RAG change processing service?')) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.post('/api/rag/changes/restart');
      setSuccess(response.data.message);
      
      // Refresh status after restart
      setTimeout(() => {
        fetchChangeProcessingStatus();
      }, 2000);
      
    } catch (error) {
      console.error('Error restarting change service:', error);
      setError(error.response?.data?.error || 'Failed to restart change service');
    } finally {
      setLoading(false);
    }
  };

  // Render status badge
  const renderStatusBadge = (status) => {
    const statusConfig = {
      'in_progress': { 
        color: isDark ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-800', 
        icon: Clock, 
        text: 'In Progress' 
      },
      'completed': { 
        color: isDark ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-800', 
        icon: CheckCircle, 
        text: 'Completed' 
      },
      'failed': { 
        color: isDark ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-800', 
        icon: XCircle, 
        text: 'Failed' 
      },
      'cleared': { 
        color: isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800', 
        icon: Trash2, 
        text: 'Cleared' 
      },
      'idle': { 
        color: isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800', 
        icon: Pause, 
        text: 'Idle' 
      }
    };

    const config = statusConfig[status] || statusConfig['idle'];
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.text}
      </span>
    );
  };

  // Render dataset card
  const renderDatasetCard = (dataset) => {
    const isEnabled = datasetConfig[dataset.name] || false;
    
    return (
      <div key={dataset.name} className={`p-6 rounded-lg border shadow-sm ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <Database className="w-5 h-5 text-blue-600 mr-2" />
            <div>
              <h3 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{dataset.display_name}</h3>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{dataset.description}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => toggleDataset(dataset.name)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                isEnabled ? 'bg-blue-600' : isDark ? 'bg-gray-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  isEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Records</p>
            <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {dataset.record_count.toLocaleString()}
            </p>
          </div>
          <div>
            <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Status</p>
            <p className={`text-sm mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {isEnabled ? (
                <span className="text-green-600 font-medium">Enabled</span>
              ) : (
                <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Disabled</span>
              )}
            </p>
          </div>
        </div>

        {dataset.error && (
          <div className={`mt-4 p-3 border rounded-md ${isDark ? 'bg-red-900/20 border-red-700' : 'bg-red-50 border-red-200'}`}>
            <div className="flex">
              <AlertCircle className="h-4 w-4 text-red-400" />
              <div className="ml-2">
                <p className={`text-sm ${isDark ? 'text-red-200' : 'text-red-800'}`}>{dataset.error}</p>
              </div>
            </div>
          </div>
        )}

        {dataset.last_updated && (
          <div className={`mt-4 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            Last updated: {new Date(dataset.last_updated).toLocaleString()}
          </div>
        )}

        {/* Detailed Statistics Section */}
        {datasetStatistics.length > 0 && (
          <div className={`mt-4 p-4 border rounded-md ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
            <div className="flex items-center justify-between mb-3">
              <h4 className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Detailed Statistics</h4>
              <button
                onClick={() => toggleStatisticsExpansion(dataset.name)}
                className={`text-xs px-2 py-1 rounded transition-colors ${
                  isDark 
                    ? 'bg-gray-600 text-gray-300 hover:bg-gray-500' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {expandedStatistics[dataset.name] ? 'Hide Details' : 'Show Details'}
              </button>
            </div>
            
            {expandedStatistics[dataset.name] && (() => {
              const stats = datasetStatistics.find(s => s.name === dataset.name);
              if (!stats || stats.error) {
                return (
                  <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {stats?.error || 'Statistics not available'}
                  </div>
                );
              }

              return (
                <div className="space-y-4">
                  {/* Key Columns Statistics */}
                  {Object.keys(stats.key_columns || {}).length > 0 && (
                    <div>
                      <h5 className={`text-xs font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Key Columns</h5>
                      <div className="grid grid-cols-1 gap-2">
                        {Object.entries(stats.key_columns).map(([column, data]) => (
                          <div key={column} className={`p-2 rounded ${isDark ? 'bg-gray-600' : 'bg-white border'}`}>
                            <div className="flex justify-between items-center mb-1">
                              <span className={`text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{column}</span>
                              <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                {data.completeness_rate}% complete
                              </span>
                            </div>
                            <div className="grid grid-cols-3 gap-2 text-xs">
                              <div>
                                <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Unique:</span>
                                <span className={`ml-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{data.unique_count?.toLocaleString()}</span>
                              </div>
                              <div>
                                <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Total:</span>
                                <span className={`ml-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{data.total_count?.toLocaleString()}</span>
                              </div>
                              <div>
                                <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Null:</span>
                                <span className={`ml-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{data.null_count?.toLocaleString()}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Numeric Statistics */}
                  {Object.keys(stats.numeric_stats || {}).length > 0 && (
                    <div>
                      <h5 className={`text-xs font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Numeric Columns</h5>
                      <div className="grid grid-cols-1 gap-2">
                        {Object.entries(stats.numeric_stats).map(([column, data]) => (
                          <div key={column} className={`p-2 rounded ${isDark ? 'bg-gray-600' : 'bg-white border'}`}>
                            <div className="flex justify-between items-center mb-1">
                              <span className={`text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{column}</span>
                              <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                {data.count?.toLocaleString()} values
                              </span>
                            </div>
                            <div className="grid grid-cols-3 gap-2 text-xs">
                              <div>
                                <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Min:</span>
                                <span className={`ml-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{data.min?.toLocaleString()}</span>
                              </div>
                              <div>
                                <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Max:</span>
                                <span className={`ml-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{data.max?.toLocaleString()}</span>
                              </div>
                              <div>
                                <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Avg:</span>
                                <span className={`ml-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{data.average?.toLocaleString()}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Date Statistics */}
                  {Object.keys(stats.date_stats || {}).length > 0 && (
                    <div>
                      <h5 className={`text-xs font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Date Columns</h5>
                      <div className="grid grid-cols-1 gap-2">
                        {Object.entries(stats.date_stats).map(([column, data]) => (
                          <div key={column} className={`p-2 rounded ${isDark ? 'bg-gray-600' : 'bg-white border'}`}>
                            <div className="flex justify-between items-center mb-1">
                              <span className={`text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{column}</span>
                              <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                {data.date_range_days ? `${data.date_range_days} days` : ''}
                              </span>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              <div>
                                <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>First:</span>
                                <span className={`ml-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                  {data.first ? new Date(data.first).toLocaleDateString() : 'N/A'}
                                </span>
                              </div>
                              <div>
                                <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Last:</span>
                                <span className={`ml-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                  {data.last ? new Date(data.last).toLocaleDateString() : 'N/A'}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* Tag Selection for Knowledge Articles */}
        {dataset.name === 'knowledge_articles' && getCollectionType(selectedCollection) === 'Sales' && isEnabled && (
          <div className={`mt-4 p-4 border rounded-md ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
            <div className="flex items-center justify-between mb-3">
              <h4 className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Select Tags/Categories for Sales Collection</h4>
              <div className="flex space-x-2">
                <button
                  onClick={selectAllTags}
                  disabled={tagsLoading}
                  className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  Select All
                </button>
                <button
                  onClick={clearAllTags}
                  disabled={tagsLoading}
                  className="text-xs px-2 py-1 bg-gray-600 text-white rounded hover:bg-gray-700 disabled:opacity-50"
                >
                  Clear All
                </button>
              </div>
            </div>
            
            {tagsLoading ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <span className={`ml-2 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Loading tags and categories...</span>
              </div>
            ) : availableTags.length === 0 ? (
              <div className="text-center py-6">
                <div className="text-gray-400 mb-2">
                  <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                </div>
                <p className={`text-sm mb-2 ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>No tags or categories found</p>
                <p className={`text-xs mb-4 ${isDark ? 'text-gray-400' : 'text-gray-400'}`}>
                  Create some knowledge articles with tags first, or use sample data for testing.
                </p>
                <div className="flex space-x-2 justify-center">
                  <button
                    onClick={createSampleData}
                    disabled={tagsLoading}
                    className="text-xs px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                  >
                    Create Sample Data
                  </button>
                  <button
                    onClick={fetchAvailableTags}
                    disabled={tagsLoading}
                    className="text-xs px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                  >
                    Refresh Tags
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="mb-3">
                  <p className={`text-xs mb-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    Selected {selectedTags.length} of {availableTags.length} available items
                    {availableTags.some(t => t.type) && (
                      <span className="ml-2">
                        ({availableTags.filter(t => t.type === 'category').length} categories, {availableTags.filter(t => t.type === 'tag').length} tags)
                      </span>
                    )}
                  </p>
                  <div className="max-h-40 overflow-y-auto">
                    <div className="grid grid-cols-1 gap-2">
                      {availableTags.map((item) => (
                        <label key={item.name} className={`flex items-center space-x-2 text-sm p-2 rounded ${isDark ? 'hover:bg-gray-600' : 'hover:bg-gray-50'}`}>
                          <input
                            type="checkbox"
                            checked={selectedTags.includes(item.name)}
                            onChange={() => toggleTag(item.name)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <div className="flex items-center justify-between flex-1">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              item.type === 'category' 
                                ? isDark ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-800' 
                                : isDark ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-800'
                            }`}>
                              {item.type === 'category' && '📂 '}
                              {item.type === 'tag' && '🏷️ '}
                              {item.name}
                            </span>
                            <span className={`text-xs ml-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                              ({item.count || 0} articles)
                            </span>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
                
                {selectedTags.length > 0 && (
                  <div className={`mt-3 p-2 rounded ${isDark ? 'bg-blue-900/20' : 'bg-blue-50'}`}>
                    <p className={`text-xs font-medium mb-1 ${isDark ? 'text-blue-300' : 'text-blue-800'}`}>Selected for Sales collection:</p>
                    <div className="flex flex-wrap gap-1">
                      {selectedTags.map((tagName) => {
                        const item = availableTags.find(t => t.name === tagName);
                        return (
                          <span key={tagName} className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${
                            item?.type === 'category' 
                              ? isDark ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-800' 
                              : isDark ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-800'
                          }`}>
                            {item?.type === 'category' && '📂 '}
                            {item?.type === 'tag' && '🏷️ '}
                            {tagName}
                            <button
                              onClick={() => toggleTag(tagName)}
                              className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-white hover:bg-opacity-50"
                            >
                              ×
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  // Render overview tab
  const renderOverviewTab = () => (
    <div className="space-y-6">
      {/* System Status */}
      <div className={`p-6 rounded-lg border shadow-sm ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>System Status</h2>
          <button
            onClick={loadData}
            disabled={loading}
            className={`inline-flex items-center px-3 py-1.5 border text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 ${
              isDark 
                ? 'border-gray-600 text-gray-300 bg-gray-700 hover:bg-gray-600' 
                : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
            }`}
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {ragStatus ? (
          <div className="space-y-4">
            {/* Status Indicators */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
              {/* Qdrant Status */}
              <div className={`p-4 rounded-lg border ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center">
                    <Database className={`w-5 h-5 mr-3 ${isDark ? 'text-gray-300' : 'text-gray-600'}`} />
                    <div>
                      <h4 className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Qdrant Service</h4>
                      <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Vector database connection</p>
                    </div>
                  </div>
                  <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                    ragStatus.system_health?.qdrant_status === 'connected' || ragStatus.system_health?.qdrant_status === 'ready'
                      ? isDark ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-800' 
                      : ragStatus.system_health?.qdrant_status === 'error' || ragStatus.system_health?.qdrant_status === 'disconnected'
                      ? isDark ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-800'
                      : isDark ? 'bg-yellow-900/30 text-yellow-300' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {ragStatus.system_health?.qdrant_status || 'Unknown'}
                  </span>
                </div>
                {ragStatus.system_health?.qdrant_url && (
                  <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    <span className="font-medium">URL:</span> {ragStatus.system_health.qdrant_url}
                  </div>
                )}
                {ragStatus.system_health?.qdrant_error && ragStatus.system_health.qdrant_status === 'disconnected' && (
                  <div className="text-xs text-red-400 mt-1">
                    <span className="font-medium">Error:</span> {ragStatus.system_health.qdrant_error}
                  </div>
                )}
                {ragStatus.system_health?.last_check && (
                  <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    <span className="font-medium">Last Check:</span> {new Date(ragStatus.system_health.last_check).toLocaleString()}
                  </div>
                )}
              </div>

              {/* Collection Status */}
              <div className={`p-4 rounded-lg border ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center">
                    <Database className={`w-5 h-5 mr-3 ${isDark ? 'text-gray-300' : 'text-gray-600'}`} />
                    <div>
                      <h4 className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{selectedCollection} Collection</h4>
                      <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Data collection status</p>
                    </div>
                  </div>
                  <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                    ragStatus.collections_info?.[selectedCollection]?.status === 'ready' 
                      ? isDark ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-800' 
                      : ragStatus.collections_info?.[selectedCollection]?.status === 'error'
                      ? isDark ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-800'
                      : ragStatus.collections_info?.[selectedCollection]?.status === 'empty'
                      ? isDark ? 'bg-yellow-900/30 text-yellow-300' : 'bg-yellow-100 text-yellow-800'
                      : isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {ragStatus.collections_info?.[selectedCollection]?.status || 'Not Found'}
                  </span>
                </div>
                <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  <span className="font-medium">Points:</span> {ragStatus.collections_info?.[selectedCollection]?.points_count?.toLocaleString() || 0}
                </div>
                {ragStatus.collections_info?.[selectedCollection]?.last_updated && (
                  <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    <span className="font-medium">Updated:</span> {new Date(ragStatus.collections_info[selectedCollection].last_updated).toLocaleString()}
                  </div>
                )}
              </div>
            </div>

            {/* Metrics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className={`text-center p-4 rounded-lg ${isDark ? 'bg-blue-900/20' : 'bg-blue-50'}`}>
                <Database className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Collection Points</p>
                <p className="text-lg font-bold text-blue-600">
                  {ragStatus.collections_info?.[selectedCollection]?.points_count || 0}
                </p>
              </div>
              <div className={`text-center p-4 rounded-lg ${isDark ? 'bg-green-900/20' : 'bg-green-50'}`}>
                <BarChart3 className="w-8 h-8 text-green-600 mx-auto mb-2" />
                <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Vector Size</p>
                <p className="text-lg font-bold text-green-600">
                  {ragStatus.system_health?.vector_size || 0}
                </p>
              </div>
              <div className={`text-center p-4 rounded-lg ${isDark ? 'bg-purple-900/20' : 'bg-purple-50'}`}>
                <Settings className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Model</p>
                <p className="text-sm font-bold text-purple-600">
                  {ragStatus.system_health?.embedding_model || 'N/A'}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
              {/* Qdrant Status - Loading */}
              <div className={`p-4 rounded-lg border ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center">
                    <Database className="w-5 h-5 text-gray-400 mr-3" />
                    <div>
                      <h4 className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Qdrant Service</h4>
                      <p className="text-xs text-gray-400">Checking connection...</p>
                    </div>
                  </div>
                  <span className={`px-3 py-1 text-xs font-medium rounded-full ${isDark ? 'bg-gray-600 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                    Loading...
                  </span>
                </div>
              </div>

              {/* Collection Status - Loading */}
              <div className={`p-4 rounded-lg border ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center">
                    <Database className="w-5 h-5 text-gray-400 mr-3" />
                    <div>
                      <h4 className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{selectedCollection} Collection</h4>
                      <p className="text-xs text-gray-400">Checking status...</p>
                    </div>
                  </div>
                  <span className={`px-3 py-1 text-xs font-medium rounded-full ${isDark ? 'bg-gray-600 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                    Loading...
                  </span>
                </div>
              </div>
            </div>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Loading RAG system status...</p>
          </div>
        )}
      </div>

      {/* Last Ingestion */}
      {ingestionStatus && ingestionStatus.collections && (
        <div className={`p-6 rounded-lg border shadow-sm ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center justify-between mb-4">
            <h2 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{selectedCollection} Ingestion Status</h2>
            <div className="flex items-center space-x-2">
              <label className={`flex items-center text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <input
                  type="checkbox"
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                  className="mr-2"
                />
                Auto-refresh
              </label>
              {renderStatusBadge(ingestionStatus.collections[selectedCollection]?.current_status)}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Current Status</p>
              <p className={`text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>{ingestionStatus.collections[selectedCollection]?.current_status || 'idle'}</p>
              {ingestionStatus.collections[selectedCollection]?.status_updated_at && (
                <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  Updated: {new Date(ingestionStatus.collections[selectedCollection].status_updated_at).toLocaleString()}
                </p>
              )}
            </div>
            <div>
              <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Collection Stats</p>
              <p className={`text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {ingestionStatus.collections[selectedCollection]?.collection_stats?.total_points?.toLocaleString() || 0} total points
              </p>
            </div>
          </div>

          {ingestionStatus.collections[selectedCollection]?.last_result && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm font-medium text-gray-900 mb-2">Last Ingestion Result</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Processed</p>
                  <p className="font-medium">{ingestionStatus.collections[selectedCollection].last_result.total_processed || 0}</p>
                </div>
                <div>
                  <p className="text-gray-500">Embedded</p>
                  <p className="font-medium">{ingestionStatus.collections[selectedCollection].last_result.total_embedded || 0}</p>
                </div>
                <div>
                  <p className="text-gray-500">Duration</p>
                  <p className="font-medium">
                    {ingestionStatus.collections[selectedCollection].last_result.duration_ms 
                      ? `${Math.round(ingestionStatus.collections[selectedCollection].last_result.duration_ms / 1000)}s`
                      : 'N/A'
                    }
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Success Rate</p>
                  <p className="font-medium">
                    {ingestionStatus.collections[selectedCollection].last_result.total_processed > 0
                      ? `${Math.round((ingestionStatus.collections[selectedCollection].last_result.total_embedded / ingestionStatus.collections[selectedCollection].last_result.total_processed) * 100)}%`
                      : 'N/A'
                    }
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* System Health */}
      <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
        <h2 className="text-lg font-medium text-gray-900 mb-4">System Health</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center space-x-3">
            <div className={`w-3 h-3 rounded-full ${ragStatus?.system_health?.qdrant_status === 'connected' ? 'bg-green-500' : 'bg-red-500'}`}></div>
            <span className="text-sm font-medium text-gray-700">Qdrant Database</span>
            <span className={`text-sm ${ragStatus?.system_health?.qdrant_status === 'connected' ? 'text-green-600' : 'text-red-600'}`}>
              {ragStatus?.system_health?.qdrant_status === 'connected' ? 'Connected' : 'Disconnected'}
            </span>
          </div>
          <div className="flex items-center space-x-3">
            <div className="w-3 h-3 rounded-full bg-blue-500"></div>
            <span className="text-sm font-medium text-gray-700">Embedding Model</span>
            <span className="text-sm text-gray-600">{ragStatus?.system_health?.embedding_model}</span>
          </div>
        </div>
        
        {/* Daily Update Information */}
        <div className="mt-4 p-3 bg-blue-50 rounded border border-blue-200">
          <div className="flex items-start space-x-3">
            <Clock className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-blue-900">Daily RAG Updates</h3>
              <p className="text-sm text-blue-700 mt-1">
                The system can be configured to automatically update RAG content daily to ensure 
                time-relevant queries (yesterday, today, tomorrow, next week) return accurate results.
              </p>
              {hasDailySchedule ? (
                <p className="text-sm text-green-700 mt-1">
                  ✅ Daily schedule is active and will run at {dailySchedule.TimeOfDay}
                </p>
              ) : (
                <p className="text-sm text-orange-700 mt-1">
                  ⚠️ No daily schedule configured. Visit the Actions tab to set up automatic updates.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Helper function to get collection type from tenant collection name
  const getCollectionType = useCallback((collectionName) => {
    const collection = tenantCollections.find(c => c.name === collectionName);
    return collection?.id || 'CRM';
  }, [tenantCollections]);

  // Render datasets tab
  const renderDatasetsTab = () => {
    // Filter datasets based on selected collection type
    const collectionType = getCollectionType(selectedCollection);
    const filteredDatasets = collectionType === 'Sales' 
      ? datasets.filter(dataset => dataset.name === 'knowledge_articles')
      : datasets;

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-medium text-gray-900">Dataset Configuration - {selectedCollection}</h2>
            {getCollectionType(selectedCollection) === 'Sales' && (
              <p className="text-sm text-gray-500 mt-1">
                Sales collection only includes knowledge articles filtered by sales-related tags
              </p>
            )}
          </div>
          <div className="flex space-x-2">
            <button
              onClick={updateDatasetConfig}
              disabled={loading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
            >
              <Settings className="w-4 h-4 mr-2" />
              Update Configuration
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredDatasets.map(renderDatasetCard)}
        </div>
      </div>
    );
  };

  // Render change tracking tab
  const renderChangesTab = () => (
    <div className="space-y-6">
      {/* Change Processing Status */}
      <div className={`p-6 rounded-lg border shadow-sm ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Change Processing Status</h2>
          <div className="flex space-x-2">
            <button
              onClick={triggerChangeProcessing}
              disabled={loading}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
            >
              <Play className="w-4 h-4 mr-1" />
              Process Changes
            </button>
            <button
              onClick={restartChangeService}
              disabled={loading}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Restart Service
            </button>
          </div>
        </div>

        {changeProcessingStatus ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className={`p-4 rounded-lg border ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
              <div className="flex items-center justify-between mb-2">
                <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Processing Status</span>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                  changeProcessingStatus.isProcessing
                    ? isDark ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-800'
                    : isDark ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-800'
                }`}>
                  {changeProcessingStatus.isProcessing ? 'Processing' : 'Idle'}
                </span>
              </div>
              <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {changeProcessingStatus.isProcessing ? 'Currently processing changes' : 'Ready to process changes'}
              </p>
            </div>

            <div className={`p-4 rounded-lg border ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
              <div className="flex items-center justify-between mb-2">
                <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Processor</span>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                  changeProcessingStatus.hasProcessingInterval
                    ? isDark ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-800'
                    : isDark ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-800'
                }`}>
                  {changeProcessingStatus.hasProcessingInterval ? 'Active' : 'Inactive'}
                </span>
              </div>
              <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {changeProcessingStatus.hasProcessingInterval ? 'Running every 10 minutes' : 'Not running'}
              </p>
            </div>

            <div className={`p-4 rounded-lg border ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
              <div className="flex items-center justify-between mb-2">
                <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Scheduler</span>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                  changeProcessingStatus.hasSchedulerInterval
                    ? isDark ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-800'
                    : isDark ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-800'
                }`}>
                  {changeProcessingStatus.hasSchedulerInterval ? 'Active' : 'Inactive'}
                </span>
              </div>
              <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {changeProcessingStatus.hasSchedulerInterval ? 'Checking schedules every minute' : 'Not running'}
              </p>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Loading change processing status...</p>
          </div>
        )}
      </div>

      {/* Change Statistics */}
      {changeStatistics && (
        <div className={`p-6 rounded-lg border shadow-sm ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center justify-between mb-4">
            <h2 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Change Statistics (Last 24 Hours)</h2>
            <button
              onClick={cleanupOldChanges}
              disabled={loading}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Cleanup Old
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Change Status */}
            <div>
              <h3 className={`text-md font-medium mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Change Status</h3>
              <div className="space-y-2">
                {changeStatistics.changes?.map((stat) => (
                  <div key={stat.ProcessingStatus} className={`flex justify-between items-center p-2 rounded ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      {stat.ProcessingStatus === 'pending' ? 'Pending' :
                       stat.ProcessingStatus === 'processing' ? 'Processing' :
                       stat.ProcessingStatus === 'completed' ? 'Completed' :
                       stat.ProcessingStatus === 'failed' ? 'Failed' : stat.ProcessingStatus}
                    </span>
                    <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {stat.Count}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Batch Status */}
            <div>
              <h3 className={`text-md font-medium mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Batch Status</h3>
              <div className="space-y-2">
                {changeStatistics.batches?.map((stat) => (
                  <div key={stat.Status} className={`flex justify-between items-center p-2 rounded ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      {stat.Status === 'pending' ? 'Pending' :
                       stat.Status === 'processing' ? 'Processing' :
                       stat.Status === 'completed' ? 'Completed' :
                       stat.Status === 'failed' ? 'Failed' : stat.Status}
                    </span>
                    <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {stat.Count} batches
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recent Changes */}
      <div className={`p-6 rounded-lg border shadow-sm ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Recent Changes</h2>
          <button
            onClick={fetchRecentChanges}
            disabled={loading}
            className={`inline-flex items-center px-3 py-1.5 border text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 ${
              isDark 
                ? 'border-gray-600 text-gray-300 bg-gray-700 hover:bg-gray-600' 
                : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
            }`}
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {recentChanges.length === 0 ? (
          <div className="text-center py-8">
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>No recent changes found</p>
          </div>
        ) : (
          <div className="space-y-3">
            {recentChanges.map((change) => (
              <div key={change.ChangeID} className={`p-3 rounded-lg border ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      change.ChangeType === 'INSERT' ? (isDark ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-800') :
                      change.ChangeType === 'UPDATE' ? (isDark ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-800') :
                      change.ChangeType === 'DELETE' ? (isDark ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-800') :
                      (isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800')
                    }`}>
                      {change.ChangeType}
                    </span>
                    <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {change.TableName}
                    </span>
                    <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      ID: {change.EntityID}
                    </span>
                  </div>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    change.ProcessingStatus === 'completed' ? (isDark ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-800') :
                    change.ProcessingStatus === 'failed' ? (isDark ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-800') :
                    change.ProcessingStatus === 'processing' ? (isDark ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-800') :
                    (isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800')
                  }`}>
                    {change.ProcessingStatus}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>
                    Tenant: {change.TenantCode || 'DEFAULT'}
                  </span>
                  <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>
                    {new Date(change.CreatedAt).toLocaleString()}
                  </span>
                </div>
                {change.ErrorMessage && (
                  <div className={`mt-2 p-2 rounded text-xs ${isDark ? 'bg-red-900/20 text-red-200' : 'bg-red-50 text-red-800'}`}>
                    Error: {change.ErrorMessage}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Processing Schedules */}
      <div className={`p-6 rounded-lg border shadow-sm ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Processing Schedules</h2>
          <button
            onClick={fetchSchedules}
            disabled={loading}
            className={`inline-flex items-center px-3 py-1.5 border text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 ${
              isDark 
                ? 'border-gray-600 text-gray-300 bg-gray-700 hover:bg-gray-600' 
                : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
            }`}
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {schedules.length === 0 ? (
          <div className="text-center py-8">
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>No schedules found</p>
          </div>
        ) : (
          <div className="space-y-3">
            {schedules.map((schedule) => (
              <div key={schedule.ScheduleID} className={`p-4 rounded-lg border ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {schedule.ScheduleName}
                    </span>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      schedule.IsActive
                        ? isDark ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-800'
                        : isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {schedule.IsActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {schedule.ScheduleType}
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                  <div>
                    <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Type:</span>
                    <span className={`ml-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {schedule.ScheduleType === 'interval' ? `${schedule.IntervalMinutes} minutes` :
                       schedule.ScheduleType === 'daily' ? `Daily at ${schedule.TimeOfDay}` :
                       schedule.ScheduleType === 'weekly' ? `Weekly on day ${schedule.DayOfWeek} at ${schedule.TimeOfDay}` :
                       schedule.ScheduleType}
                    </span>
                  </div>
                  <div>
                    <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Last Run:</span>
                    <span className={`ml-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {schedule.LastRunAt ? new Date(schedule.LastRunAt).toLocaleString() : 'Never'}
                    </span>
                  </div>
                  <div>
                    <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Next Run:</span>
                    <span className={`ml-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {schedule.NextRunAt ? new Date(schedule.NextRunAt).toLocaleString() : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // Render statistics tab
  const renderStatisticsTab = () => (
    <div className="space-y-6">
      <div className={`p-6 rounded-lg border shadow-sm ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Dataset Statistics</h2>
          <button
            onClick={fetchDatasetStatistics}
            disabled={statisticsLoading}
            className={`inline-flex items-center px-3 py-1.5 border text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 ${
              isDark 
                ? 'border-gray-600 text-gray-300 bg-gray-700 hover:bg-gray-600' 
                : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
            }`}
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${statisticsLoading ? 'animate-spin' : ''}`} />
            Refresh Statistics
          </button>
        </div>

        {statisticsLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className={`ml-3 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Loading detailed statistics...</span>
          </div>
        ) : datasetStatistics.length === 0 ? (
          <div className="text-center py-8">
            <BarChart3 className={`mx-auto h-12 w-12 ${isDark ? 'text-gray-400' : 'text-gray-300'}`} />
            <h3 className={`mt-2 text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>No statistics available</h3>
            <p className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Click "Refresh Statistics" to load detailed dataset information.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Summary Statistics */}
            <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
              <h3 className={`text-md font-medium mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Summary</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className={`p-3 rounded ${isDark ? 'bg-gray-600' : 'bg-white border'}`}>
                  <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {datasetStatistics.reduce((sum, stat) => sum + (stat.countall || 0), 0).toLocaleString()}
                  </div>
                  <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>Total Records</div>
                </div>
                <div className={`p-3 rounded ${isDark ? 'bg-gray-600' : 'bg-white border'}`}>
                  <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {datasetStatistics.filter(stat => stat.countall > 0).length}
                  </div>
                  <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>Datasets with Data</div>
                </div>
                <div className={`p-3 rounded ${isDark ? 'bg-gray-600' : 'bg-white border'}`}>
                  <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {datasetStatistics.filter(stat => stat.error).length}
                  </div>
                  <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>Datasets with Errors</div>
                </div>
              </div>
            </div>

            {/* Individual Dataset Statistics */}
            <div className="space-y-4">
              {datasetStatistics.map((stat) => (
                <div key={stat.name} className={`p-4 rounded-lg border ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className={`text-md font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{stat.display_name}</h4>
                      <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{stat.description}</p>
                    </div>
                    <div className="text-right">
                      <div className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {stat.countall?.toLocaleString() || 0}
                      </div>
                      <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Total Records</div>
                    </div>
                  </div>

                  {stat.error ? (
                    <div className={`p-3 border rounded-md ${isDark ? 'bg-red-900/20 border-red-700' : 'bg-red-50 border-red-200'}`}>
                      <div className="flex">
                        <AlertCircle className="h-4 w-4 text-red-400" />
                        <div className="ml-2">
                          <p className={`text-sm ${isDark ? 'text-red-200' : 'text-red-800'}`}>{stat.error}</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Key Columns */}
                      {Object.keys(stat.key_columns || {}).length > 0 && (
                        <div>
                          <h5 className={`text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Key Columns</h5>
                          <div className="space-y-2">
                            {Object.entries(stat.key_columns).map(([column, data]) => (
                              <div key={column} className={`p-2 rounded ${isDark ? 'bg-gray-600' : 'bg-gray-50'}`}>
                                <div className="flex justify-between items-center mb-1">
                                  <span className={`text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{column}</span>
                                  <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                    {data.completeness_rate}% complete
                                  </span>
                                </div>
                                <div className="grid grid-cols-3 gap-1 text-xs">
                                  <div>
                                    <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Unique:</span>
                                    <span className={`ml-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{data.unique_count?.toLocaleString()}</span>
                                  </div>
                                  <div>
                                    <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Total:</span>
                                    <span className={`ml-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{data.total_count?.toLocaleString()}</span>
                                  </div>
                                  <div>
                                    <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Null:</span>
                                    <span className={`ml-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{data.null_count?.toLocaleString()}</span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Numeric Statistics */}
                      {Object.keys(stat.numeric_stats || {}).length > 0 && (
                        <div>
                          <h5 className={`text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Numeric Columns</h5>
                          <div className="space-y-2">
                            {Object.entries(stat.numeric_stats).map(([column, data]) => (
                              <div key={column} className={`p-2 rounded ${isDark ? 'bg-gray-600' : 'bg-gray-50'}`}>
                                <div className="flex justify-between items-center mb-1">
                                  <span className={`text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{column}</span>
                                  <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                    {data.count?.toLocaleString()} values
                                  </span>
                                </div>
                                <div className="grid grid-cols-3 gap-1 text-xs">
                                  <div>
                                    <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Min:</span>
                                    <span className={`ml-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{data.min?.toLocaleString()}</span>
                                  </div>
                                  <div>
                                    <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Max:</span>
                                    <span className={`ml-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{data.max?.toLocaleString()}</span>
                                  </div>
                                  <div>
                                    <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Avg:</span>
                                    <span className={`ml-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{data.average?.toLocaleString()}</span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Date Statistics */}
                      {Object.keys(stat.date_stats || {}).length > 0 && (
                        <div>
                          <h5 className={`text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Date Columns</h5>
                          <div className="space-y-2">
                            {Object.entries(stat.date_stats).map(([column, data]) => (
                              <div key={column} className={`p-2 rounded ${isDark ? 'bg-gray-600' : 'bg-gray-50'}`}>
                                <div className="flex justify-between items-center mb-1">
                                  <span className={`text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{column}</span>
                                  <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                    {data.date_range_days ? `${data.date_range_days} days` : ''}
                                  </span>
                                </div>
                                <div className="grid grid-cols-2 gap-1 text-xs">
                                  <div>
                                    <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>First:</span>
                                    <span className={`ml-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                      {data.first ? new Date(data.first).toLocaleDateString() : 'N/A'}
                                    </span>
                                  </div>
                                  <div>
                                    <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Last:</span>
                                    <span className={`ml-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                      {data.last ? new Date(data.last).toLocaleDateString() : 'N/A'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // Render actions tab
  const renderActionsTab = () => (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Data Management Actions - {selectedCollection}</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <button
            onClick={() => startIngestion(false)}
            disabled={loading || ingestionStatus?.collections?.[selectedCollection]?.current_status === 'in_progress'}
            className="flex items-center justify-center px-4 py-3 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50"
          >
            <Play className="w-4 h-4 mr-2" />
            Start Ingestion
          </button>

          <button
            onClick={() => startIngestion(true)}
            disabled={loading || ingestionStatus?.collections?.[selectedCollection]?.current_status === 'in_progress'}
            className="flex items-center justify-center px-4 py-3 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-50"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Force Refresh
          </button>

          <button
            onClick={triggerTimeRelevantUpdate}
            disabled={loading}
            className="flex items-center justify-center px-4 py-3 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
          >
            <Clock className="w-4 h-4 mr-2" />
            Time-Relevant Update
          </button>

          <button
            onClick={clearData}
            disabled={loading}
            className="flex items-center justify-center px-4 py-3 border border-gray-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Clear All Data
          </button>

          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center justify-center px-4 py-3 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            {showAdvanced ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
            {showAdvanced ? 'Hide' : 'Show'} Advanced
          </button>
        </div>

        {/* Daily Schedule Management */}
        <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h3 className="text-md font-medium text-blue-900 mb-4">🕐 Daily RAG Update Schedule</h3>
          <p className="text-sm text-blue-700 mb-4">
            Enable automatic daily updates to ensure the RAG system can answer time-relevant queries 
            about yesterday, today, tomorrow, next week, etc.
          </p>
          
          {hasDailySchedule ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-white rounded border">
                <div>
                  <p className="font-medium text-gray-900">{dailySchedule.ScheduleName}</p>
                  <p className="text-sm text-gray-600">
                    Daily at {dailySchedule.TimeOfDay} • 
                    Status: <span className={`font-medium ${dailySchedule.IsActive ? 'text-green-600' : 'text-red-600'}`}>
                      {dailySchedule.IsActive ? 'Active' : 'Inactive'}
                    </span>
                  </p>
                  {dailySchedule.NextRunAt && (
                    <p className="text-sm text-gray-500">
                      Next run: {new Date(dailySchedule.NextRunAt).toLocaleString()}
                    </p>
                  )}
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={triggerTimeRelevantUpdate}
                    disabled={loading}
                    className="px-3 py-1 text-xs font-medium text-blue-700 bg-blue-100 hover:bg-blue-200 rounded"
                  >
                    Run Now
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-700">
                  No daily schedule configured. Create one to enable automatic time-relevant updates.
                </p>
              </div>
              <button
                onClick={createDailySchedule}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
              >
                Create Daily Schedule
              </button>
            </div>
          )}
        </div>

        {showAdvanced && (
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-md font-medium text-gray-900 mb-4">Advanced Configuration</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Qdrant URL</label>
                <input
                  type="text"
                  value={ragStatus?.system_health?.qdrant_url || ''}
                  readOnly
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-600 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Embedding Model</label>
                <input
                  type="text"
                  value={ragStatus?.system_health?.embedding_model || ''}
                  readOnly
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-600 text-sm"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Test Results */}
      {/* Removed Test Results section */}
    </div>
  );

  // Render search tab
  const renderSearchTab = () => (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Search {selectedCollection} Data</h2>
        
        <div className="flex space-x-2 mb-4">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={`Enter search query for ${selectedCollection} collection...`}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            onKeyPress={(e) => e.key === 'Enter' && searchRAG()}
          />
          <button
            onClick={searchRAG}
            disabled={searchLoading || !searchQuery.trim()}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
          >
            <Search className={`w-4 h-4 mr-2 ${searchLoading ? 'animate-spin' : ''}`} />
            Search
          </button>
        </div>

        {searchResults && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-md font-medium text-gray-900">
                Search Results ({searchResults.total_results})
              </h3>
              <p className="text-sm text-gray-500">Query: "{searchResults.query}"</p>
            </div>

            {searchResults.results.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No results found.</p>
            ) : (
              <div className="space-y-3">
                {searchResults.results.map((result, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {result.type}
                      </span>
                      <span className="text-sm text-gray-500">
                        Score: {(result.score * 100).toFixed(1)}%
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 mb-2">{result.content}</p>
                    <div className="text-xs text-gray-500">
                      {result.metadata ? (
                        <>
                          Entity ID: {result.metadata.entity_id || 'N/A'} | 
                          Table: {result.metadata.source_table || 'N/A'} |
                          Created: {result.metadata.created_at ? new Date(result.metadata.created_at).toLocaleDateString() : 'N/A'}
                        </>
                      ) : (
                        <>
                          ID: {result.id || 'N/A'} | 
                          Collection: {selectedCollection} |
                          Type: {result.type || 'N/A'}
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  // Create daily RAG update schedule
  const createDailySchedule = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await axios.post('/api/rag/changes/schedules/daily', {
        timeOfDay: '02:00', // Default to 2 AM
        collections: [selectedCollection]
      });
      
      setSuccess('Daily RAG update schedule created successfully');
      fetchSchedules(); // Refresh schedules list
      
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to create daily schedule');
    } finally {
      setLoading(false);
    }
  };

  // Trigger time-relevant update
  const triggerTimeRelevantUpdate = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await axios.post('/api/rag/update/time-relevant', {
        collection: selectedCollection,
        tenantCode: getTenantCode()
      });
      
      setSuccess('Time-relevant RAG update started successfully. This will ensure the system can answer queries about recent data, yesterday, today, tomorrow, next week, etc.');
      
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to trigger time-relevant update');
    } finally {
      setLoading(false);
    }
  };

  // Check if daily schedule exists
  const hasDailySchedule = schedules.some(schedule => 
    schedule.ScheduleName === 'Daily RAG Update' && schedule.ScheduleType === 'daily'
  );

  // Get daily schedule
  const dailySchedule = schedules.find(schedule => 
    schedule.ScheduleName === 'Daily RAG Update' && schedule.ScheduleType === 'daily'
  );

  if (loading && !ragStatus) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Action Bar */}
      <div className="flex items-center justify-between">
        <div>
          <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Configure and manage the Retrieval-Augmented Generation system for enhanced AI chat
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Collection:</label>
          <select
            value={selectedCollection}
            onChange={(e) => setSelectedCollection(e.target.value)}
            className={`px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              isDark 
                ? 'border-gray-600 bg-gray-700 text-white' 
                : 'border-gray-300 bg-white text-gray-900'
            }`}
          >
            {tenantCollections.map((collection) => (
              <option key={collection.name} value={collection.name}>
                {collection.displayName}
              </option>
            ))}
          </select>
          <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            Tenant: {getTenantCode()}
          </div>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className={`mb-6 p-4 border rounded-md ${isDark ? 'bg-red-900/20 border-red-700' : 'bg-red-50 border-red-200'}`}>
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className={`text-sm ${isDark ? 'text-red-200' : 'text-red-800'}`}>{error}</p>
            </div>
          </div>
        </div>
      )}

      {success && (
        <div className={`mb-6 p-4 border rounded-md ${isDark ? 'bg-green-900/20 border-green-700' : 'bg-green-50 border-green-200'}`}>
          <div className="flex">
            <CheckCircle className="h-5 w-5 text-green-400" />
            <div className="ml-3">
              <p className={`text-sm ${isDark ? 'text-green-200' : 'text-green-800'}`}>{success}</p>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="mb-6">
        <nav className="flex space-x-8" aria-label="Tabs">
          {[
            { id: 'overview', name: 'Overview', icon: BarChart3 },
            { id: 'datasets', name: 'Datasets', icon: Database },
            { id: 'statistics', name: 'Statistics', icon: BarChart3 },
            { id: 'changes', name: 'Change Tracking', icon: RefreshCw },
            { id: 'actions', name: 'Actions', icon: Settings },
            { id: 'search', name: 'Search', icon: Search }
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : isDark 
                      ? 'border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
              >
                <Icon className="w-4 h-4 mr-2" />
                {tab.name}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="space-y-6">
        {activeTab === 'overview' && renderOverviewTab()}
        {activeTab === 'datasets' && renderDatasetsTab()}
        {activeTab === 'statistics' && renderStatisticsTab()}
        {activeTab === 'changes' && renderChangesTab()}
        {activeTab === 'actions' && renderActionsTab()}
        {activeTab === 'search' && renderSearchTab()}
      </div>
    </div>
  );
};

export default RAGManagement; 